import React, { useEffect, useState } from 'react';
import {
    Box,
    Grid,
    OutlinedInput,
    Paper,
    Typography,
    Stack,
    Divider,
    Chip,
    Tooltip,
    TextField,
    IconButton,
    Button,
    InputLabel,
    FormControl,
    RadioGroup,
    FormLabel,
    FormControlLabel,
    Radio,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { isValid } from 'date-fns';
import { toDate, formatDate } from '../../../utils/dateHelpers';
import AddPlanModal from '../AddPlanModal';
import TextMaskCustom from '../../Shared/PhoneMask';
import { copyObject } from '../../../utils/common';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { RootState } from '../../../reducers';
import { PlanItemTypeEnum } from '../../../Enum/PlanItemTypeEnum';
import { PlanStatusEnum } from '../../../Enum/PlanStatusEnum';
import { setShowMessage } from '../../../store/shared.slice';
import { CarePlanTypeEnum } from '../../../Enum/CarePlanTypeEnum';
import {
    fetchBehavioralPlanByMemberId,
    getDefaultCareCircle,
    saveBehavioralPlan,
    saveCareCircleItem,
    savePlanPDFToDocuments,
    setConfirmItemBehavioralPlan,
    setConfirmOpenBehavioralPlan,
    setConfirmTypeBehavioralPlan,
    setEditIdBehavioralPlan,
    setFieldValuesBehavioralPlan,
    setModalStateBehavioralPlan,
    setModalTitleBehavioralPlan,
} from '../../../store/behaviorOrSafetyPlanSlice.slice';
import { useParams } from 'react-router-dom';
import Authorization from '../../../utils/Authorization';
import { PlanItem } from '../../../Models/CarePlans/PlanItem.model';

const SECTION_CONFIG = [
    {
        key: 'Behaviors',
        sectionLabel: 'Behaviors',
        title: 'Step 1: What behaviors do I want to improve?',
        addLabel: 'Add Behavior',
        fields: [{ name: 'Note', label: 'Note', multiline: true }],
    },
    {
        key: 'Triggers',
        sectionLabel: 'Triggers',
        title: 'Step 2: What are the triggers and stressors that lead to my behaviors I want to improve (behaviors, situations, circumstances) that put me at emotional risk?',
        addLabel: 'Add Trigger',
        fields: [{ name: 'Note', label: 'Note', multiline: true }],
    },
    {
        key: 'WarningSigns',
        sectionLabel: 'Warning Signs',
        title: 'Step 3: What are the warning signs (thoughts, images, mood, situation, behavior) that may interfere with my behavior plan?',
        addLabel: 'Add Warning Sign',
        fields: [{ name: 'Note', label: 'Note', multiline: true }],
    },
    {
        key: 'Strategies',
        sectionLabel: 'Coping Strategies',
        title: 'Step 4: Internal coping strategies: What can I do to take my mind off my triggers without contacting another person (relaxation techniques, physical activity, hobbies, etc.).',
        addLabel: 'Add Strategy',
        fields: [{ name: 'Note', label: 'Note', multiline: true }],
    },
    {
        key: 'PositiveDistractions',
        sectionLabel: 'Positive Distractions',
        title: 'Step 5: Who are some people/what are some social settings that provide me with positive distractions (family, friends, AA/NA/GA, etc.)?',
        addLabel: 'Add Positive Distraction',
        fields: [
            { name: 'Type', label: 'Type' },
            { name: 'Name', label: 'Name' },
            { name: 'Phone', label: 'Phone', mask: true },
            { name: 'Note', label: 'Note', multiline: true },
        ],
    },
    {
        key: 'People',
        sectionLabel: 'Helpful People',
        title: 'Step 6: People whom I can ask for help:',
        addLabel: 'Add Person',
        fields: [
            { name: 'Type', label: 'Type' },
            { name: 'Name', label: 'Name' },
            { name: 'Phone', label: 'Phone', mask: true },
            { name: 'Note', label: 'Note', multiline: true },
        ],
    },
    {
        key: 'Agencies',
        sectionLabel: 'Professionals/Agencies',
        title: 'Step 7: Professionals or Agencies I can contact during a crisis:',
        addLabel: 'Add Professional/Agency',
        fields: [
            { name: 'Type', label: 'Type' },
            { name: 'Name', label: 'Name' },
            { name: 'Phone', label: 'Phone', mask: true },
            { name: 'Note', label: 'Note', multiline: true },
        ],
    },
    {
        key: 'Avoids',
        sectionLabel: 'Avoids',
        title: 'Step 8: I should avoid the following people, places, or things:',
        addLabel: 'Add Avoid',
        fields: [{ name: 'Note', label: 'Note', multiline: true }],
    },
];

function getInitialFieldValues(fields) {
    const obj = {};
    fields.forEach((f) => {
        obj[f.name] = '';
    });
    return obj;
}

const Section = ({ sectionLabel, title, items, onAdd, renderItem, addLabel, gridProps = null }) => (
    <Grid size={{ xs: 12, md: 6 }} {...gridProps}>
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                {sectionLabel}
            </Typography>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body1" fontWeight="bold">
                    {title}
                </Typography>
                <Tooltip title={addLabel}>
                    <IconButton onClick={onAdd} sx={{ ml: 2 }} color="primary">
                        <AddCircleOutlineIcon fontSize="large" />
                    </IconButton>
                </Tooltip>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            {items?.length === 0 ? (
                <Typography color="text.secondary" fontStyle="italic">
                    No entries yet.
                </Typography>
            ) : (
                <Stack spacing={2}>{items.map(renderItem)}</Stack>
            )}
        </Paper>
    </Grid>
);

const ItemCard = ({ note, createdAt, createdBy, updatedAt, updatedBy, onEdit, onDelete, extraFields = null }) => (
    <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box sx={{ flex: 1 }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {note}
                </Typography>
                {extraFields}
                <Stack direction="row" spacing={2} mt={1}>
                    <Typography variant="caption" color="text.secondary">
                        Updated: {updatedAt ? formatDate(updatedAt, 'MM/dd/yyyy hh:mm a') : '-'} {updatedBy?.Name && `by ${updatedBy.Name}`}
                    </Typography>
                </Stack>
            </Box>
            <Stack direction="row" spacing={1}>
                <Tooltip title="Edit">
                    <IconButton onClick={onEdit}>
                        <EditIcon color="primary" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                    <IconButton onClick={onDelete}>
                        <CancelIcon color="error" />
                    </IconButton>
                </Tooltip>
            </Stack>
        </Stack>
    </Paper>
);

const BehavioralPlan = () => {
    const {
        behavioralPlanInfo,
        modalTitleBehavioralPlan,
        modalStateBehavioralPlan,
        editIdBehavioralPlan,
        fieldValuesBehavioralPlan,
        confirmOpenBehavioralPlan,
        confirmItemBehavioralPlan,
        confirmTypeBehavioralPlan,
    } = useSelector((state: RootState) => state.safetyPlanSlice);
    const dispatch = useAppDispatch();
    const authData = new Authorization();
    const params = useParams();

    const [careCircleConfirmOpen, setCareCircleConfirmOpen] = useState(false);
    const [pendingCareCircleItem, setPendingCareCircleItem] = useState(null);
    const [pendingCareCircleSection, setPendingCareCircleSection] = useState(null);
    const [showDeclineReason, setShowDeclineReason] = useState(false);
    const [declineReason, setDeclineReason] = useState('');
    const [showConfirm, setShowConfirm] = useState(false);

    useEffect(() => {
        dispatch(fetchBehavioralPlanByMemberId(params.memberId));
    }, [dispatch, params.memberId]);

    const isBehavioralPlanComplete = () => {
        return SECTION_CONFIG.every((section) => {
            if (Array.isArray(behavioralPlanInfo[section.key])) {
                return behavioralPlanInfo[section.key].length > 0;
            }
            return Boolean(behavioralPlanInfo[section.key]);
        });
    };

    const handlePlanPromptAccept = () => {
        const behavioralPlanCopy = copyObject(behavioralPlanInfo);
        behavioralPlanCopy.CarePlanType = CarePlanTypeEnum.BehavioralPlan;
        behavioralPlanCopy.Status = PlanStatusEnum.Created;
        behavioralPlanCopy.MemberId = params.memberId;
        dispatch(saveBehavioralPlan(behavioralPlanCopy));
    };

    const handleShowDeclineReason = () => {
        setShowDeclineReason(true);
    };
    const handleCloseDeclineReason = () => {
        setShowDeclineReason(false);
        setDeclineReason('');
    };

    const handleSaveDocument = () => {
        dispatch(savePlanPDFToDocuments(behavioralPlanInfo.Id));
        setShowConfirm(false);
    };

    const handlePlanPromptDecline = () => {
        const behavioralPlanCopy = copyObject(behavioralPlanInfo);
        behavioralPlanCopy.CarePlanType = CarePlanTypeEnum.BehavioralPlan;
        behavioralPlanCopy.Status = PlanStatusEnum.Declined;
        behavioralPlanCopy.DeclinedReason = declineReason;
        behavioralPlanCopy.MemberId = params.memberId;
        behavioralPlanCopy.DeclinedBy = {
            Id: authData.UserId,
            Name: authData.UserName,
        };
        dispatch(saveBehavioralPlan(behavioralPlanCopy));
        handleCloseDeclineReason();
    };

    // Generic handlers
    const handleAdd = (section) => {
        dispatch(setEditIdBehavioralPlan({ ...editIdBehavioralPlan, [section.key]: null }));
        dispatch(setFieldValuesBehavioralPlan({ ...fieldValuesBehavioralPlan, [section.key]: getInitialFieldValues(section.fields) }));
        dispatch(setModalStateBehavioralPlan({ ...modalStateBehavioralPlan, [section.key]: true }));
        dispatch(setModalTitleBehavioralPlan(`Add ${section.sectionLabel}`));
    };

    const handleEdit = (section, row) => {
        dispatch(setEditIdBehavioralPlan({ ...editIdBehavioralPlan, [section.key]: row.Id }));
        const values = {};
        section.fields.forEach((f) => {
            values[f.name] = row[f.name] || '';
        });
        dispatch(setFieldValuesBehavioralPlan({ ...fieldValuesBehavioralPlan, [section.key]: values }));
        dispatch(setModalStateBehavioralPlan({ ...modalStateBehavioralPlan, [section.key]: true }));
        dispatch(setModalTitleBehavioralPlan(`Edit ${section.sectionLabel}`));
    };
    const handleSubmit = (section) => {
        const values = fieldValuesBehavioralPlan[section.key];
        if (section.fields.some((f) => !String(values[f.name] ?? '').trim())) {
            setShowMessage(true, `Please enter all required fields.`, 'error');
            return;
        }
        if (!editIdBehavioralPlan[section.key] && values.Type === '1') {
            setPendingCareCircleItem({ ...values });
            setPendingCareCircleSection(section);
            setCareCircleConfirmOpen(true);
            return;
        }
        if (editIdBehavioralPlan[section.key]) {
            const itemsCopy = copyObject(behavioralPlanInfo[section.key]);
            const existing = itemsCopy.find((item) => item.Id === editIdBehavioralPlan[section.key]);
            section.fields.forEach((f) => {
                existing[f.name] = values[f.name];
            });
            const behavioralPlanCopy = copyObject(behavioralPlanInfo);
            behavioralPlanCopy[section.key] = itemsCopy;
            dispatch(saveBehavioralPlan(behavioralPlanCopy));
        } else {
            const newItem = new PlanItem();
            section.fields.forEach((f) => {
                newItem[f.name] = values[f.name];
            });
            const behavioralPlanCopy = copyObject(behavioralPlanInfo);
            behavioralPlanCopy[section.key] = [...behavioralPlanCopy[section.key], newItem];
            dispatch(saveBehavioralPlan(behavioralPlanCopy));
        }
        dispatch(setFieldValuesBehavioralPlan({ ...fieldValuesBehavioralPlan, [section.key]: getInitialFieldValues(section.fields) }));
        dispatch(setEditIdBehavioralPlan({ ...editIdBehavioralPlan, [section.key]: null }));
        dispatch(setModalStateBehavioralPlan({ ...modalStateBehavioralPlan, [section.key]: false }));
    };

    const handleCareCircleConfirm = (addToCareCircle) => {
        if (addToCareCircle) {
            const careCircleItem = getDefaultCareCircle();
            careCircleItem.Name = pendingCareCircleItem.Name;
            careCircleItem.HomePhone = pendingCareCircleItem.Phone.replace(/[^\d.]/g, '');
            dispatch(saveCareCircleItem(careCircleItem, params.memberId));
        }
        const section = pendingCareCircleSection;
        const newItem = new PlanItem();
        newItem.IsCareCircle = true;
        section.fields.forEach((f) => {
            newItem[f.name] = pendingCareCircleItem[f.name];
        });
        const behavioralPlanCopy = copyObject(behavioralPlanInfo);
        behavioralPlanCopy[section.key] = [...behavioralPlanCopy[section.key], newItem];
        dispatch(saveBehavioralPlan(behavioralPlanCopy));
        dispatch(setFieldValuesBehavioralPlan({ ...fieldValuesBehavioralPlan, [section.key]: getInitialFieldValues(section.fields) }));
        dispatch(setEditIdBehavioralPlan({ ...editIdBehavioralPlan, [section.key]: null }));
        dispatch(setModalStateBehavioralPlan({ ...modalStateBehavioralPlan, [section.key]: false }));
        setCareCircleConfirmOpen(false);
        setPendingCareCircleItem(null);
        setPendingCareCircleSection(null);
    };

    const handleDelete = (row, type) => {
        openConfirm(row, type);
    };

    const openConfirm = (item, type) => {
        dispatch(setConfirmItemBehavioralPlan(item));
        dispatch(setConfirmTypeBehavioralPlan(type));
        dispatch(setConfirmOpenBehavioralPlan(true));
    };
    const closeConfirm = () => {
        dispatch(setConfirmOpenBehavioralPlan(false));
        dispatch(setConfirmItemBehavioralPlan(null));
        dispatch(setConfirmTypeBehavioralPlan(''));
    };
    const handleConfirmDelete = () => {
        if (!confirmItemBehavioralPlan) return;
        const itemsCopy = copyObject(behavioralPlanInfo[confirmTypeBehavioralPlan]);
        const index = itemsCopy.findIndex((item) => item.Id === confirmItemBehavioralPlan.Id);
        if (index === -1) {
            console.error('Item not found in section:', confirmTypeBehavioralPlan);
            closeConfirm();
            return;
        }
        itemsCopy.splice(index, 1);
        const behavioralPlanCopy = copyObject(behavioralPlanInfo);
        behavioralPlanCopy[confirmTypeBehavioralPlan] = itemsCopy;
        dispatch(saveBehavioralPlan(behavioralPlanCopy));
        closeConfirm();
    };
    return (
        <Box sx={{ padding: 2, minHeight: '80vh', pb: 5 }}>
            <Dialog open={showDeclineReason} disableEscapeKeyDown maxWidth="md" fullWidth onClose={() => setShowDeclineReason(false)}>
                <DialogTitle>Decline Behavioral Plan?</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="decline-reason"
                        label="Reason for Declining"
                        type="text"
                        multiline
                        rows={4}
                        required
                        fullWidth
                        variant="outlined"
                        value={declineReason}
                        onChange={(e) => setDeclineReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeclineReason} color="primary" variant="outlined" sx={{ minWidth: '120px' }}>
                        Cancel
                    </Button>
                    <Button disabled={!declineReason} onClick={handlePlanPromptDecline} color="primary" variant="contained" sx={{ minWidth: '120px' }}>
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
            {behavioralPlanInfo?.Status === PlanStatusEnum.NotStarted && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                        No Behavioral Plan found for this member. Would you like to create a Behavioral Plan now?
                    </Typography>
                    <Stack direction="row" spacing={2} mt={2}>
                        <Button variant="outlined" sx={{ minWidth: '120px' }} color="primary" onClick={handleShowDeclineReason}>
                            Decline
                        </Button>
                        <Button variant="contained" sx={{ minWidth: '120px' }} color="primary" onClick={handlePlanPromptAccept}>
                            Create Behavioral Plan
                        </Button>
                    </Stack>
                </Paper>
            )}
            {behavioralPlanInfo?.Status === PlanStatusEnum.Declined && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                        Behavioral Plan creation was declined by <b>{behavioralPlanInfo?.DeclinedBy?.Name}</b> on{' '}
                        <b>{(() => { const d = toDate(behavioralPlanInfo?.DeclinedAt); return d ? formatDate(d, 'MM/dd/yyyy') : ''; })()}</b>.{' '}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                        Reason for Decline: <b>{behavioralPlanInfo.DeclinedReason || ' - '}</b>
                    </Typography>
                    <Button sx={{ mt: 2 }} variant="contained" color="primary" onClick={handlePlanPromptAccept}>
                        Create Behavioral Plan
                    </Button>
                </Paper>
            )}
            {behavioralPlanInfo?.Status === PlanStatusEnum.Created && (
                <>
                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                        <Typography variant="h5" fontWeight="bold">
                            Behavioral Plan
                        </Typography>
                        <Tooltip title="Behavior Plan help page.">
                            <InfoIcon color="primary" />
                        </Tooltip>
                        <Box sx={{ flex: 1 }} />
                        {Boolean(behavioralPlanInfo?.UpdatedBy) ? (
                            <Chip
                                size="small"
                                label={`Last Reviewed: ${behavioralPlanInfo.UpdatedBy?.Name} - ${behavioralPlanInfo.UpdatedAt ? (() => { const d = toDate(behavioralPlanInfo.UpdatedAt); return d ? formatDate(d, 'MM/dd/yyyy') : ''; })() : ''
                                    }`}
                            />
                        ) : (
                            <Chip size="small" label={`Last Reviewed: -`} />
                        )}
                    </Stack>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={2}>
                        {SECTION_CONFIG.map((section) => (
                            <Section
                                key={section.key}
                                sectionLabel={section.sectionLabel}
                                title={section.title}
                                items={behavioralPlanInfo[section.key]}
                                onAdd={() => handleAdd(section)}
                                addLabel={section.addLabel}
                                renderItem={(row, idx) => (
                                    <ItemCard
                                        key={row.Id || idx}
                                        note={section.fields.length === 1 ? row.Note : ''}
                                        createdAt={row.CreatedAt}
                                        createdBy={row.CreatedBy}
                                        updatedAt={row.UpdatedAt}
                                        updatedBy={row.UpdatedBy}
                                        onEdit={() => handleEdit(section, row)}
                                        onDelete={() => handleDelete(row, section.key)}
                                        extraFields={
                                            section.fields.length > 1 && (
                                                <>
                                                    {row.Name && (
                                                        <Typography variant="body2">
                                                            <b>Name:</b> {row.Name}
                                                        </Typography>
                                                    )}
                                                    {row.Phone && <Typography variant="body2">Phone: {row.Phone}</Typography>}
                                                    {row.Note && <Typography variant="body2">Note: {row.Note}</Typography>}
                                                </>
                                            )
                                        }
                                    />
                                )}
                            />
                        ))}
                    </Grid>
                    {SECTION_CONFIG.map((section) => (
                        <AddPlanModal
                            key={section.key}
                            open={!!modalStateBehavioralPlan[section.key]}
                            onClose={() => {
                                dispatch(setModalStateBehavioralPlan({ ...modalStateBehavioralPlan, [section.key]: false }));
                                dispatch(setEditIdBehavioralPlan({ ...editIdBehavioralPlan, [section.key]: null }));
                                dispatch(setFieldValuesBehavioralPlan({ ...fieldValuesBehavioralPlan, [section.key]: getInitialFieldValues(section.fields) }));
                            }}
                            title={modalTitleBehavioralPlan}
                            onSubmit={() => handleSubmit(section)}
                            isEdit={!!editIdBehavioralPlan[section.key]}
                        >
                            <Stack spacing={2} pt={2}>
                                {section.fields.map((f) =>
                                    f.name === 'Type' ? (
                                        <FormControl component="fieldset" fullWidth key={f.name} sx={{ mt: 1 }}>
                                            <FormLabel component="legend">{f.label}</FormLabel>
                                            <RadioGroup
                                                row
                                                name={`type-radio-${section.key}`}
                                                value={fieldValuesBehavioralPlan[section.key]?.[f.name] || ''}
                                                onChange={(e) =>
                                                    dispatch(
                                                        setFieldValuesBehavioralPlan({
                                                            ...fieldValuesBehavioralPlan,
                                                            [section.key]: { ...fieldValuesBehavioralPlan[section.key], [f.name]: e.target.value },
                                                        })
                                                    )
                                                }
                                            >
                                                <FormControlLabel value={PlanItemTypeEnum.Person} control={<Radio />} label="Person" />
                                                <FormControlLabel value={PlanItemTypeEnum.Place} control={<Radio />} label="Place" />
                                            </RadioGroup>
                                        </FormControl>
                                    ) : f.mask ? (
                                        <FormControl fullWidth key={f.name}>
                                            <InputLabel htmlFor={`formatted-text-mask-input-${section.key}`}>{f.label}</InputLabel>
                                            <OutlinedInput
                                                value={fieldValuesBehavioralPlan[section.key]?.[f.name] || ''}
                                                onChange={(e) =>
                                                    dispatch(
                                                        setFieldValuesBehavioralPlan({
                                                            ...fieldValuesBehavioralPlan,
                                                            [section.key]: { ...fieldValuesBehavioralPlan[section.key], [f.name]: e.target.value },
                                                        })
                                                    )
                                                }
                                                name={f.name}
                                                required
                                                label={f.label}
                                                id={`formatted-text-mask-input-${section.key}`}
                                                inputComponent={TextMaskCustom as any}
                                            />
                                        </FormControl>
                                    ) : (
                                        <TextField
                                            key={f.name}
                                            autoFocus={f.name === section.fields[0].name}
                                            margin="dense"
                                            id={f.name}
                                            label={f.label}
                                            multiline={!!f.multiline}
                                            rows={f.multiline ? 3 : 1}
                                            type="text"
                                            fullWidth
                                            variant="outlined"
                                            value={fieldValuesBehavioralPlan[section.key]?.[f.name] || ''}
                                            onChange={(e) =>
                                                dispatch(
                                                    setFieldValuesBehavioralPlan({
                                                        ...fieldValuesBehavioralPlan,
                                                        [section.key]: { ...fieldValuesBehavioralPlan[section.key], [f.name]: e.target.value },
                                                    })
                                                )
                                            }
                                        />
                                    )
                                )}
                            </Stack>
                        </AddPlanModal>
                    ))}
                </>
            )}
            <Dialog open={confirmOpenBehavioralPlan} onClose={closeConfirm}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>Do you wish to delete this item?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeConfirm} color="primary" variant="outlined" sx={{ minWidth: '120px' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained" sx={{ minWidth: '120px' }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={careCircleConfirmOpen} onClose={() => handleCareCircleConfirm(false)}>
                <DialogTitle>Add to Care Circle?</DialogTitle>
                <DialogContent>
                    <DialogContentText>Would you like to add this person to the member's Care Circle?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleCareCircleConfirm(false)} color="primary" variant="outlined" sx={{ minWidth: '120px' }}>
                        No
                    </Button>
                    <Button onClick={() => handleCareCircleConfirm(true)} color="primary" variant="contained" sx={{ minWidth: '120px' }}>
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Save PDF to Documents Confirmation Dialog */}
            <Dialog open={showConfirm} onClose={() => setShowConfirm(false)}>
                <DialogTitle>Save PDF to Documents?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <Typography variant="body1">Would you like to save the PDF to the Documents folder?</Typography>
                        {!isBehavioralPlanComplete() && (
                            <Typography variant="body1" color="error">
                                {' '}
                                Not all fields are complete.
                            </Typography>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowConfirm(false)} color="primary" variant="outlined" sx={{ minWidth: '120px' }}>
                        No
                    </Button>
                    <Button onClick={handleSaveDocument} color="primary" variant="contained" sx={{ minWidth: '120px' }}>
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
            {behavioralPlanInfo.Status !== PlanStatusEnum.NotStarted && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: { xs: 16, md: 32 },
                        right: { xs: 16, md: 32 },
                        zIndex: 1300,
                    }}
                >
                    <Button
                        variant="contained"
                        color="success"
                        size="small"
                        sx={{
                            boxShadow: 4,
                            borderRadius: 8,
                            px: 2,
                            py: 1,
                            fontWeight: 'bold',
                        }}
                        onClick={() => setShowConfirm(true)}
                    >
                        Save PDF to Documents
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default BehavioralPlan;
